/**
 * ForgotPasswordPage Component
 * STORY-009: Password Reset
 * BUG-007: Fixed logo display - replaced "LOGO" text with AuthLogo component
 * STORY-104: Language Selection on Login Page
 *
 * Page component for requesting a password reset email.
 * Located at /forgot-password in the application.
 */

import React, { useState, useCallback, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { passwordResetService } from '../services/authService';
import { AuthLogo } from '../components/auth';
import { LanguageSelector } from '../components/navigation/LanguageSelector';
import './AuthPages.css';

/**
 * Form state interface
 */
interface FormState {
  email: string;
}

/**
 * ForgotPasswordPage Component
 */
export const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation('auth');

  // Form state
  const [formData, setFormData] = useState<FormState>({ email: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  /**
   * Handle input change
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      // Clear error when user starts typing
      if (error) setError(null);
    },
    [error]
  );

  /**
   * Validate email format
   */
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      // Validate email
      if (!formData.email.trim()) {
        setError(t('forgotPassword.errors.emailRequired'));
        return;
      }

      if (!validateEmail(formData.email)) {
        setError(t('forgotPassword.errors.invalidEmail'));
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        await passwordResetService.forgotPassword({ email: formData.email });
        setIsSuccess(true);
      } catch (err) {
        // Even if an error occurs, we don't want to reveal if the email exists
        // So we show a generic success message
        // Only show error for network/server errors
        if (err instanceof Error && err.message.includes('Network')) {
          setError(t('forgotPassword.errors.network'));
        } else {
          // Show success anyway to prevent email enumeration
          setIsSuccess(true);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [formData.email, t]
  );

  /**
   * Handle resend email
   */
  const handleResend = useCallback(async () => {
    setIsResending(true);
    setResendSuccess(false);

    try {
      await passwordResetService.forgotPassword({ email: formData.email });
      setResendSuccess(true);
    } catch {
      // Silently handle error - still show success for security
      setResendSuccess(true);
    } finally {
      setIsResending(false);
    }
  }, [formData.email]);

  // Success state
  if (isSuccess) {
    return (
      <div className="auth-page">
        {/* STORY-104: Language Selector - positioned in top-right corner */}
        <div className="auth-page__language-selector">
          <LanguageSelector variant="icon" />
        </div>

        <div className="auth-container">
          {/* Logo - BUG-007: Using shared AuthLogo component for consistency */}
          <AuthLogo data-testid="forgot-password-auth-logo" />

          <div className="auth-header">
            <h1 className="auth-title">{t('forgotPassword.success.title')}</h1>
          </div>

          <div className="auth-success confirmation-message">
            <div className="auth-success__icon" aria-hidden="true">
              ✓
            </div>
            <p className="auth-success__message success-message">
              {t('forgotPassword.success.message', { email: formData.email })}
            </p>
            <p className="auth-success__expiry">
              {t('forgotPassword.success.expiry', { time: t('forgotPassword.success.expiryTime') })}
            </p>
            <p className="auth-success__hint">
              {t('forgotPassword.success.spamWarning')}
            </p>
          </div>

          {/* Resend Email Button */}
          <div className="auth-actions">
            {resendSuccess ? (
              <p className="auth-resend-success" role="status">
                <span aria-hidden="true">✓</span> {t('forgotPassword.success.resent')}
              </p>
            ) : (
              <button
                type="button"
                className="auth-button auth-button--secondary"
                onClick={handleResend}
                disabled={isResending}
              >
                {isResending ? (
                  <>
                    <span className="auth-button__spinner" aria-hidden="true" />
                    {t('forgotPassword.resend.sending')}
                  </>
                ) : (
                  t('forgotPassword.resend.button')
                )}
              </button>
            )}
          </div>

          <div className="auth-links">
            <Link to="/login" className="auth-link">
              {t('forgotPassword.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      {/* STORY-104: Language Selector - positioned in top-right corner */}
      <div className="auth-page__language-selector">
        <LanguageSelector variant="icon" />
      </div>

      <div className="auth-container">
        {/* Logo - BUG-007: Using shared AuthLogo component for consistency */}
        <AuthLogo data-testid="forgot-password-auth-logo" />

        <div className="auth-header">
          <h1 className="auth-title">{t('forgotPassword.title')}</h1>
          <p className="auth-subtitle">
            {t('forgotPassword.subtitle')}
          </p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {/* Error Message */}
          {error && (
            <div className="auth-error" role="alert">
              <span className="auth-error__icon" aria-hidden="true">
                !
              </span>
              {error}
            </div>
          )}

          {/* Email Field */}
          <div className="auth-field">
            <label htmlFor="email" className="auth-label">
              {t('forgotPassword.email')}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`auth-input ${error ? 'auth-input--error' : ''}`}
              placeholder={t('forgotPassword.emailPlaceholder')}
              autoComplete="email"
              autoFocus
              required
              disabled={isLoading}
              aria-describedby={error ? 'email-error' : undefined}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="auth-button auth-button--primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="auth-button__spinner" aria-hidden="true" />
                {t('forgotPassword.submitting')}
              </>
            ) : (
              t('forgotPassword.submit')
            )}
          </button>
        </form>

        {/* Links */}
        <div className="auth-links">
          <Link to="/login" className="auth-link">
            {t('forgotPassword.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
